import React, { useState, useEffect } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { Account, AccountSnapshot } from '../types';
import { accountsAPI } from '../services/api';
import { formatCurrency, formatDate } from '../utils/formatters';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useScrollLock } from '../hooks/useScrollLock';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import InstitutionLogo from './InstitutionLogo';
import UpdateValueModal from './UpdateValueModal';

interface AccountHistoryProps {
  account: Account;
  onClose: () => void;
  onUpdate: () => void;
}

const AccountHistory: React.FC<AccountHistoryProps> = ({ account, onClose, onUpdate }) => {
  const [snapshots, setSnapshots] = useState<AccountSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [editingSnapshot, setEditingSnapshot] = useState<AccountSnapshot | undefined>(undefined);

  useEffect(() => { fetchSnapshots(); }, [account.id]);
  useScrollLock(true);

  const fetchSnapshots = async () => {
    try {
      setLoading(true);
      const data = await accountsAPI.getAccountSnapshots(account.id);
      setSnapshots(data);
    } catch (err) {
      console.error('Error fetching snapshots:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (snapshotId: number) => {
    if (!window.confirm('Delete this value? This cannot be undone.')) return;
    try {
      await accountsAPI.deleteSnapshot(snapshotId);
      await fetchSnapshots();
      onUpdate();
    } catch (err) {
      console.error('Error deleting snapshot:', err);
    }
  };

  const openAdd = () => {
    setEditingSnapshot(undefined);
    setUpdateModalOpen(true);
  };

  const openEdit = (snapshot: AccountSnapshot) => {
    setEditingSnapshot(snapshot);
    setUpdateModalOpen(true);
  };

  const handleSaved = async () => {
    await fetchSnapshots();
    onUpdate();
  };

  // Chart
  const chartData = [...snapshots].reverse().map(s => ({ date: formatDate(s.date), value: s.value }));

  const getYAxisDomain = () => {
    if (snapshots.length === 0) return [0, 1000];
    const values = snapshots.map(s => s.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const pad = (max - min) * 0.15 || max * 0.1;
    return [min - pad, max + pad];
  };

  const formatYAxisTick = (v: number) => {
    const a = Math.abs(v);
    if (a >= 1_000_000) return `${v < 0 ? '-' : ''}$${(a / 1_000_000).toFixed(1)}M`;
    if (a >= 1_000) return `${v < 0 ? '-' : ''}$${(a / 1_000).toFixed(0)}k`;
    return `$${v.toFixed(0)}`;
  };

  return (
    <>
      <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent className="max-w-4xl w-full max-h-[88vh] overflow-hidden flex flex-col p-0 gap-0">

          {/* Header */}
          <DialogHeader className="px-6 pt-5 pb-4 border-b border-[var(--border-color)] flex-shrink-0">
            <div className="flex items-center gap-3">
              <InstitutionLogo institution={account.institution} plaidLogoUrl={account.plaid_logo_url} />
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-base font-semibold leading-tight">
                  {account.institution}
                </DialogTitle>
                <p className="text-sm text-muted-foreground mt-0.5">{account.name}</p>
              </div>
            </div>
          </DialogHeader>

          {/* Hero strip */}
          <div className="px-6 py-4 border-b border-[var(--border-color)] flex-shrink-0 flex items-center gap-8">
            <div>
              <div className="text-xs font-medium uppercase tracking-wide mb-1 text-muted-foreground">Current Value</div>
              <div className="text-2xl font-bold" style={{ color: 'var(--success-color)' }}>
                {loading ? '—' : formatCurrency(account.current_value || 0)}
              </div>
            </div>
            <div className="w-px h-10 bg-[var(--border-color)]" />
            <div>
              <div className="text-xs font-medium uppercase tracking-wide mb-1 text-muted-foreground">Last Updated</div>
              <div className="text-sm font-semibold">
                {account.latest_snapshot_date ? formatDate(account.latest_snapshot_date) : 'Never'}
              </div>
            </div>
            <div className="w-px h-10 bg-[var(--border-color)]" />
            <div>
              <div className="text-xs font-medium uppercase tracking-wide mb-1 text-muted-foreground">Type</div>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white"
                style={{ backgroundColor: 'var(--primary-color)' }}>
                {account.account_type.charAt(0).toUpperCase() + account.account_type.slice(1).toLowerCase()}
              </span>
            </div>
            <div className="ml-auto">
              <Button size="sm" onClick={openAdd}>+ Add Value</Button>
            </div>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

            {/* Chart */}
            {snapshots.length > 1 && (
              <div className="rounded-xl border border-[var(--border-color)] bg-white/[0.03] p-5">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-4">Value over time</h3>
                <div className="h-48 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="date" stroke="var(--text-muted)" fontSize={11} tick={{ fill: 'var(--text-muted)' }} />
                      <YAxis tickFormatter={formatYAxisTick} domain={getYAxisDomain()} stroke="var(--text-muted)" fontSize={11} tick={{ fill: 'var(--text-muted)' }} width={60} />
                      <Tooltip
                        formatter={(value: any) => [formatCurrency(value), 'Value']}
                        labelFormatter={(label) => `Date: ${label}`}
                        contentStyle={{
                          backgroundColor: 'var(--background-card)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          color: 'var(--text-primary)',
                          fontSize: '13px'
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="var(--primary-color)"
                        strokeWidth={2}
                        dot={{ fill: 'var(--primary-color)', strokeWidth: 0, r: 3 }}
                        activeDot={{ r: 5, fill: 'var(--primary-color)' }}
                        animationDuration={300}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* History table */}
            <div className="rounded-xl border border-[var(--border-color)] bg-white/[0.03] overflow-hidden">
              <div className="px-5 py-3.5 border-b border-[var(--border-color)] flex items-center justify-between">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">History</h3>
                <span className="text-xs text-muted-foreground">
                  {snapshots.length} {snapshots.length === 1 ? 'entry' : 'entries'}
                </span>
              </div>

              {loading ? (
                <div className="px-5 py-8 text-center text-sm text-muted-foreground">Loading…</div>
              ) : snapshots.length === 0 ? (
                <div className="px-5 py-8 text-center">
                  <p className="text-sm text-muted-foreground">No values yet.</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">Add your first value to start tracking history.</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--border-color)]">
                      <th className="text-left px-5 py-2.5 text-xs font-medium text-muted-foreground">Date</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Value</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Notes</th>
                      <th className="px-4 py-2.5 w-16" />
                    </tr>
                  </thead>
                  <tbody>
                    {snapshots.map((snapshot, idx) => (
                      <tr
                        key={snapshot.id}
                        className={`border-b border-[var(--border-color)]/60 last:border-0 hover:bg-white/[0.02] transition-colors ${idx === 0 ? 'bg-white/[0.02]' : ''}`}
                      >
                        <td className="px-5 py-3 tabular-nums" style={{ color: 'var(--text-primary)' }}>
                          {formatDate(snapshot.date)}
                        </td>
                        <td className="px-4 py-3 font-semibold tabular-nums" style={{ color: 'var(--success-color)' }}>
                          {formatCurrency(snapshot.value)}
                          {idx === 0 && (
                            <span className="ml-2 text-[10px] font-medium text-muted-foreground bg-white/10 px-1.5 py-0.5 rounded">latest</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">{snapshot.notes || ''}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 justify-end">
                            <button
                              onClick={() => openEdit(snapshot)}
                              className="p-1.5 rounded transition-colors"
                              style={{ color: 'var(--text-muted)' }}
                              onMouseEnter={e => (e.currentTarget.style.color = 'var(--text-primary)')}
                              onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted)')}
                              title="Edit"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => handleDelete(snapshot.id)}
                              className="p-1.5 rounded transition-colors"
                              style={{ color: 'var(--text-muted)' }}
                              onMouseEnter={e => (e.currentTarget.style.color = 'var(--danger-color)')}
                              onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted)')}
                              title="Delete"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

          </div>
        </DialogContent>
      </Dialog>

      <UpdateValueModal
        account={account}
        snapshot={editingSnapshot}
        open={updateModalOpen}
        onClose={() => setUpdateModalOpen(false)}
        onSaved={handleSaved}
      />
    </>
  );
};

export default AccountHistory;
